import { Command, CommandsRegistry, ICommandCanExecuteReturnValue, UndoableCommand } from '../../commands/common/commands.js';
import { Emitter } from '../../../base/common/events.js';
import { isHierarchyNode, IHierarchyNode, IHierarchyService } from './hierarchy.js';
import { HierarchyNode } from './hierarchyService.js';
import { ILogService } from '../../log/common/log.js';
import { ServicesAccessor } from '../../instantiation/common/instantiation.js';
import { safeStringify } from '../../../base/common/objects.js';
import { IClipboardService } from '../../clipboard/common/clipboardService.js';
import { ContextMenuContext, isContextMenuContext } from '../../contextview/common/context.js';
import { MenuId, MenuRegistry } from '../../actions/common/actions.js';
import { INotificationService } from '../../notifications/common/notifications.js';
import { IAnalyticsService } from '../../analytics/common/analytics.js';
import { IStorageService, StorageScope, StorageTarget } from '../../storage/common/storage.js';



export interface SelectNodeCommandArgs {
	node: IHierarchyNode | null | string | number;
	options?: {
		multiSelect?: boolean;
		toggle?: boolean;
	};
}


// #region SelectNodeCommand

/**
 * Command to select one or more nodes in the tree.
 * Handles single selection, multi-selection, and deselection.
 */
export class SelectNodeCommand extends UndoableCommand<SelectNodeCommandArgs, { selection: IHierarchyNode[] }> {
	static readonly ID = 'hierarchy.select.node';

	private static STORAGE_ID = 'hierarchy.select.nodes';
	static IS_SELECTED(id: number | string, storage: IStorageService): boolean {
		const prev = storage.getObject(this.STORAGE_ID, StorageScope.WORKSPACE) as Array<number | string> | undefined;
		if (prev?.includes(id)) {
			return true;
		}
		return false;
	}
	private static SET_SELECTED(ids: Array<number | string>, storage: IStorageService): void {
		storage.store(this.STORAGE_ID, ids, StorageScope.WORKSPACE, StorageTarget.USER);
	}

	constructor(
		private readonly prov: IHierarchyService,
		private readonly onSelectedNodesDidChange: Emitter<{ selection: IHierarchyNode[] }>
	) {
		super(SelectNodeCommand.ID, 'Select Node', {

			description: 'Select one or more nodes in the tree!',
			args: {
				name: 'args',
				schema: {
					type: 'object',
					description: 'The arguments for selecting the node',
					properties: {
						node: {
							type: "string",
							description: 'The node ID to select'
						},
						options: {
							type: 'object',
							description: 'Selection options',
							properties: {
								multiSelect: {
									type: 'boolean',
									description: 'Whether to multi-select'
								},
								toggle: {
									type: 'boolean',
									description: 'Whether to toggle selection'
								}
							}
						}
					}
				}
			}
		});
	}

	private previousSelection: IHierarchyNode[] = [];
	private expandedNodesOnPerform: string[] | null = null;

	canExecute(args: SelectNodeCommandArgs): ICommandCanExecuteReturnValue {
		let { node, options } = args;

		if (!this.prov.findById) {
			return { canExecute: false, reason: 'No tree hierarchy available' };
		}

		if (typeof node === 'string' || typeof node === 'number') {
			node = this.prov.findById(String(node));
			if (!node) {
				return { canExecute: false, reason: 'Node not found' };
			}
		}
		else if (node === null) {
			return { canExecute: true };
		}
		else if (node === undefined) {
			return { canExecute: false, reason: 'Node is undefined' };
		}
		// sometimes AI seems to call { id: 'nodeId' }
		else if ((!isHierarchyNode(node) && "id" in node)) {
			node = this.prov.findById(String((node as any).id));
			if (!node) {
				return { canExecute: false, reason: 'Node not found*' };
			}
		}
		else if (!isHierarchyNode(node)) {
			return { canExecute: false, reason: 'Node is not a valid tree node' };
		}
		return { canExecute: true };
	}

	protected perform(accessor: ServicesAccessor, args: SelectNodeCommandArgs): { selection: IHierarchyNode[] } {

		if (!this.prov) {
			throw new Error('No tree hierarchy available');
		}


		let { node, options } = args;

		if (typeof node === 'string' || typeof node === 'number') {
			node = this.prov.findById(String(node));
		}

		const multiSelect = options?.multiSelect ?? false;
		const toggle = options?.toggle ?? false;

		// Store previous selection for undo
		this.previousSelection = Array.from(this.prov.getSelectedNodes());

		let newSelection: IHierarchyNode[];

		if (node) {
			if (multiSelect) {
				// Multi-select: add/remove from selection
				if (toggle && node.selected) {
					// Deselect this node
					newSelection = this.previousSelection.filter(n => n !== node);
				} else {
					// Add to selection
					newSelection = [...this.previousSelection, node];
				}
			} else {
				// Single select: replace selection
				if (toggle && node.selected) {
					// Toggle off - deselect all
					newSelection = [];
				} else {
					newSelection = [node];
				}
			}
		}
		else {
			newSelection = [];
		}

		// Update all nodes
		for (const n of this.prov.iterateNodes()) {
			n.setWithoutNotify("selected", newSelection.includes(n));
		}
		const res = this.expandSelectionToParents(newSelection);
		this.expandedNodesOnPerform = res ? res.expandedNodes : null;

		// Fire single change event
		this.onSelectedNodesDidChange.fire({ selection: newSelection });

		// Notify changes
		for (const n of newSelection) {
			n.notifyChanged();
		}
		for (const n of this.previousSelection) {
			if (!newSelection.includes(n)) {
				n.notifyChanged();
			}
		}

		SelectNodeCommand.SET_SELECTED(newSelection.map(n => n.id), accessor.get(IStorageService));
		accessor.get(IStorageService).flush();

		return { selection: newSelection };
	}

	executeForAI(accessor: ServicesAccessor, args: SelectNodeCommandArgs): string {
		const result = this.perform(accessor, args);
		return safeStringify({ selected: result.selection.map(s => s.id) });
	};


	protected undo(accessor: ServicesAccessor): void {

		if (!this.prov) {
			throw new Error('No tree hierarchy available');
		}

		const storage = accessor.get(IStorageService);

		// Restore previous selection
		for (const n of this.prov.iterateNodes()) {
			n.setWithoutNotify("selected", this.previousSelection.includes(n));
		}

		if (this.expandedNodesOnPerform && this.expandedNodesOnPerform.length > 0) {
			for (const node of this.prov.iterateNodes()) {
				if (this.expandedNodesOnPerform.includes(node.id)) {
					node.setWithoutNotify("expanded", false);
				}
			}
		}

		// Notify changes
		for (const n of this.previousSelection) {
			n.notifyChanged();
		}

		this.onSelectedNodesDidChange.fire({ selection: this.previousSelection });

		SelectNodeCommand.SET_SELECTED(this.previousSelection.map(n => n.id), storage);
		storage.flush();
	}

	private expandSelectionToParents(nodes: IHierarchyNode[]): { expandedNodes: string[] } | null {

		let expandedNodeIds: string[] | null = null;

		for (const node of nodes) {
			let current: IHierarchyNode | null = node;
			while (current) {
				if (current.expanded === false && current !== node) {
					current.setWithoutNotify("expanded", true);
					expandedNodeIds = expandedNodeIds || [];
					expandedNodeIds.push(current.id);
				}
				current = current.parent;
			}
		}

		return expandedNodeIds ? { expandedNodes: expandedNodeIds } : null;
	}
}



// #region RenameNodeCommand
export interface RenameNodeCommandArgs {
	node: IHierarchyNode | string;
	newName: string;
}

/**
 * Command to rename a node.
 * Syncs the name to the underlying object if it has a name property.
 */
export class RenameNodeCommand extends UndoableCommand<RenameNodeCommandArgs> {
	static readonly ID = 'tree.renameNode';


	constructor(
		private readonly hierarchy: IHierarchyService,
	) {
		super(RenameNodeCommand.ID, 'Rename Node', {
			description: 'Rename a node in the tree',
			args: {
				name: 'args',
				schema: {
					type: 'object',
					description: 'The arguments for renaming the node',
					properties: {
						node: {
							type: ['object', 'string'],
							description: 'The TreeNode or its ID to rename'
						},
						newName: {
							type: 'string',
							description: 'The new name for the node'
						}
					},
					required: ['node', 'newName']
				}
			},
			returns: 'void',
		});
	}

	private oldName: string = '';

	canExecute(args: RenameNodeCommandArgs): ICommandCanExecuteReturnValue {
		let { node, newName } = args;

		if (typeof node === 'string') {
			// Assume string is ID and can be found in the tree
			return { canExecute: true };
		}
		else if (!isHierarchyNode(node)) {
			return { canExecute: false, reason: 'Invalid node' };
		}
		else if (typeof newName !== 'string' || newName.trim().length === 0) {
			return { canExecute: false, reason: 'Invalid new name. The name must be a string > 0' };
		}
		return { canExecute: true };
	}

	protected perform(accessor: ServicesAccessor, args: RenameNodeCommandArgs): void {
		let { node, newName } = args;

		if (typeof node === 'string') {
			const found = this.hierarchy.findById(node);
			if (!found) {
				throw new Error('Node not found');
			}
			node = found;
		}

		// Store old name for undo
		this.oldName = node.name;

		// Update TreeNode
		node.setWithoutNotify("name", newName);

		// Sync to underlying object
		const object = node.owner;
		if (object && 'name' in object) {
			(object as any).name = newName;
		}

		// Notify changes
		node.notifyChanged();
	}

	protected undo(accessor: ServicesAccessor, args: RenameNodeCommandArgs): void {
		this.perform(accessor, {
			node: args.node,
			newName: this.oldName
		});
	}
}


// #region SetNodeExpandedCommand

export type SetNodeExpandedCommandArgs = {
	node: IHierarchyNode | string;
	expanded: boolean;
	recursive?: boolean;
}

/**
 * Command to toggle node expansion.
 */
export class SetNodeExpandedCommand extends UndoableCommand<SetNodeExpandedCommandArgs> {
	static readonly ID = 'tree.setNodeExpanded';


	private static STORAGE_ID = (id: number | string) => 'tree.nodeExpanded.' + id;

	static getStorageState(id: IHierarchyNode, storage: IStorageService): boolean | undefined {
		return storage.getBoolean(SetNodeExpandedCommand.STORAGE_ID(Number(id.id)), StorageScope.WORKSPACE);
	}
	static setStorageState(id: IHierarchyNode, expanded: boolean, storage: IStorageService): void {
		storage.store(SetNodeExpandedCommand.STORAGE_ID(Number(id.id)), expanded, StorageScope.WORKSPACE, StorageTarget.USER);
	}
	static clearStorageState(id: IHierarchyNode, storage: IStorageService): void {
		storage.remove(SetNodeExpandedCommand.STORAGE_ID(Number(id.id)), StorageScope.WORKSPACE);
	}


	constructor(
		private readonly hierarchy: IHierarchyService
	) {
		super(SetNodeExpandedCommand.ID, 'Toggle Expanded');
	}

	canExecute(args: SetNodeExpandedCommandArgs): ICommandCanExecuteReturnValue {
		let { node, expanded } = args;

		if (typeof node === 'string') {
			// Assume string is ID and can be found in the tree
			return { canExecute: true };
		}
		if (!node) {
			return { canExecute: false, reason: 'Node not found' };
		}
		else if (!isHierarchyNode(node)) {
			return { canExecute: false, reason: 'Invalid node' };
		}
		else if (node.expanded === expanded) {
			return { canExecute: false, reason: 'Node already in desired expanded state' };
		}

		return { canExecute: true };
	}

	private _didModifyExpandState: string[] | null = null;

	protected async perform(accessor: ServicesAccessor, args: SetNodeExpandedCommandArgs) {
		const storage = accessor.get(IStorageService);

		let { node, expanded } = args;

		if (typeof node === 'string') {
			const found = this.hierarchy.findById(node);
			if (!found) {
				throw new Error('Node not found');
			}
			node = found;
		}
		if (args.recursive) {
			this._didModifyExpandState = [];
			this.updateExpandedStateRecursively(node, expanded, storage, this._didModifyExpandState);
		}
		else {
			node.setWithoutNotify("expanded", expanded);
			SetNodeExpandedCommand.setStorageState(node, expanded, storage);
		}

		node.notifyChanged();
		storage.flush();
	}

	protected undo(accessor: ServicesAccessor, args: SetNodeExpandedCommandArgs): void {
		const storage = accessor.get(IStorageService);

		let { node, expanded } = args;
		if (typeof node === 'string') {
			const found = this.hierarchy.findById(node);
			if (!found) {
				throw new Error('Node not found');
			}
			node = found;
		}
		node.setWithoutNotify("expanded", !expanded);
		SetNodeExpandedCommand.setStorageState(node, !expanded, storage);

		if (this._didModifyExpandState && this._didModifyExpandState.length > 0) {
			for (const node of this.hierarchy.iterateNodes()) {
				if (this._didModifyExpandState.includes(node.id)) {
					node.setWithoutNotify("expanded", !expanded);
					SetNodeExpandedCommand.setStorageState(node, !expanded, storage);

				}
			}
		}
		node.notifyChanged();
		storage.flush();
	}

	private updateExpandedStateRecursively(node: IHierarchyNode, expanded: boolean, storage: IStorageService, list: string[] | null = null): void {
		node.setWithoutNotify("expanded", expanded);
		SetNodeExpandedCommand.setStorageState(node, expanded, storage);

		list?.push(node.id);
		for (const child of node.children) {
			this.updateExpandedStateRecursively(child, expanded, storage, list);

		}
	}
}

export type DeleteNodeCommandArgs = {
	node: IHierarchyNode | string;
}


// #region DeleteNodeCommand

/**
 * Command to delete a node from the tree.
 * Stores the node, parent, and index for undo.
 */
export class DeleteNodeCommand extends UndoableCommand<DeleteNodeCommandArgs> {
	static readonly ID = 'tree.deleteNode';

	private deletedNode: IHierarchyNode | null = null;
	private parent: IHierarchyNode | null = null;
	private indexInParent: number = -1;
	private wasSelected: boolean = false;

	constructor(
		private readonly hierarchy: IHierarchyService,
		private readonly onSelectedNodesDidChange: Emitter<{ selection: IHierarchyNode[] }>
	) {
		super(DeleteNodeCommand.ID, 'Delete Node', {
			description: 'Delete a node from the hierarchy',
			args: {
				name: 'args',
				schema: {
					type: 'object',
					description: 'The arguments for deleting the node',
					properties: {
						node: {
							type: ['string'],
							description: 'The id of the Node to delete'
						}
					},
					required: ['node']
				}
			},
			returns: 'void',
		});
	}

	canExecute(args: DeleteNodeCommandArgs): ICommandCanExecuteReturnValue {
		let { node } = args;

		if (typeof node === 'string') {
			// Assume string is ID and can be found in the tree
			return { canExecute: true };
		}
		else if (!isHierarchyNode(node)) {
			return { canExecute: false, reason: 'Invalid node' };
		}
		return { canExecute: true };
	}

	protected perform(accessor: ServicesAccessor, args: DeleteNodeCommandArgs): void {
		const logger = accessor.get(ILogService);
		logger.info("Delete node command...");

		let { node } = args;

		if (typeof node === 'string') {
			const found = this.hierarchy.findById(node);
			if (!found) {
				throw new Error('Node not found');
			}
			node = found;
		}

		// Store info for undo
		this.deletedNode = node;
		this.parent = node.parent;
		this.indexInParent = node.parent ? node.parent.children.indexOf(node) : -1
		this.wasSelected = node.selected;

		// Deselect if selected
		if (node.selected) {
			node.setWithoutNotify("selected", false);
			const selectedNodes = Array.from(this.hierarchy.getSelectedNodes());
			this.onSelectedNodesDidChange.fire({ selection: selectedNodes });
		}

		// Remove from parent
		// this.hierarchy.remove(node);
		this.hierarchy.remove(node);
		// this.parent?.removeChild(node);

		node.notifyChanged();

		// Notify changes
		if (this.parent) {
			this.parent.notifyChanged();
		}
	}

	protected undo(accessor: ServicesAccessor, args: DeleteNodeCommandArgs): void {
		// let { node: targetNode } = node;

		if (!this.deletedNode) {
			console.warn('Cannot undo delete: missing node or parent');
			return;
		}

		if (!this.parent) {
			// if the node had no parent, re-add it to the hierarchy roots
			this.hierarchy.add(this.deletedNode);
		}
		// Re-insert at the same index
		else if (this.indexInParent >= 0 && this.indexInParent < this.parent.children.length) {
			this.parent.addChild(this.deletedNode, this.indexInParent);
		} else {
			// Append at the end
			this.parent.addChild(this.deletedNode);
		}

		// Restore selection if it was selected
		if (this.wasSelected) {
			this.deletedNode.setWithoutNotify("selected", true);
			const selectedNodes = Array.from(this.hierarchy.getSelectedNodes());
			this.onSelectedNodesDidChange.fire({ selection: selectedNodes });
		}

		this.deletedNode.notifyChanged();
	}
}




// #region NavigateHierarchyCommand

type NavigateHierarchyCommandArgs = {
	direction: 'up' | 'down' | 'left' | 'right';
}

export class NavigateHierarchyCommand extends UndoableCommand<NavigateHierarchyCommandArgs> {

	static readonly ID = 'hierarchy.navigate';

	constructor(
		private readonly hierarchy: IHierarchyService,
		@ILogService private readonly logger: ILogService,
	) {
		super(NavigateHierarchyCommand.ID, 'Navigate the hierarchy tree', {
			description: 'Navigate the hierarchy tree (down/up/left/right)',
		});
	}

	canExecute(args: NavigateHierarchyCommandArgs): ICommandCanExecuteReturnValue {
		return { canExecute: true };
	}

	protected perform(accessor: ServicesAccessor, args: NavigateHierarchyCommandArgs): void {
		this.hierarchy.navigate(args.direction);
	}

	protected undo(accessor: ServicesAccessor, args: NavigateHierarchyCommandArgs): void {
		switch (args.direction) {
			case 'up':
				this.hierarchy.navigate('down');
				break;
			case 'down':
				this.hierarchy.navigate('up');
				break;
			case 'left':
				this.hierarchy.navigate('right');
				break;
			case 'right':
				this.hierarchy.navigate('left');
				break;
		}
	}
}




// #region CopyHierarchyCommand



export interface CopyHierarchyCommandArgs {

}

export class CopyHierarchyCommand extends Command<CopyHierarchyCommandArgs> {

	static readonly ID = 'hierarchy.copy';

	constructor(
	) {
		super(CopyHierarchyCommand.ID, 'Copy Hierarchy to Clipboard', {
			description: 'Copy the entire hierarchy as JSON to the clipboard',
		});
	}

	canExecute(args: unknown) {
		return { canExecute: true };
	}

	async execute(accessor: ServicesAccessor, args: unknown) {

		const hierarchyService = accessor.get(IHierarchyService);
		const logger = accessor.get(ILogService);
		const clipboard = accessor.get(IClipboardService);
		const notification = accessor.get(INotificationService);
		const telemetry = accessor.get(IAnalyticsService);

		if (!hierarchyService) {
			telemetry.logEvent('hierarchy.copy-json.failed', { reason: 'no hierarchy service available' });
			logger.error('Hierarchy service not available.');
			notification.showNotification({
				type: 'error',
				message: 'Cannot copy: Hierarchy not available.'
			})
			return;
		}

		const serializeNode = (node: IHierarchyNode): any => {
			const obj: any = {
				id: node.id,
				name: node.name,
				type: node.type,
				children: node.children.map(child => serializeNode(child))
			};
			return obj;
		};

		let nodes: IHierarchyNode[] | null = null;
		let showNotification = false;

		if (isContextMenuContext(args)) {
			telemetry.logEvent('hierarchy.copy-json', { source: 'contextMenu' });
			// First try to find a hierarchy node in the targets
			const target = args.targets?.find(t => isHierarchyNode(t));
			if (target && isHierarchyNode(target)) {
				nodes = [target];
				showNotification = true;
			}
			// If no hierarchy node found, check if we have a hierarchy service in the targets (for header context menu)
			else {
				const hierarchyServiceTarget = args.targets?.find(t => t && typeof t === 'object' && 'roots' in t);
				if (hierarchyServiceTarget) {
					// Called from hierarchy header - copy selected nodes or all roots
					nodes = hierarchyService.getSelectedNodes();
					if (!nodes?.length) nodes = hierarchyService.roots;
					showNotification = true;
				} else {
					logger.error('No valid hierarchy node or service found in context menu targets.');
					notification.showNotification({
						key: 'copyHierarchyNoNode',
						type: 'error',
						message: 'Cannot copy: No valid hierarchy context found.',
						position: 'cursor'
					});
					return;
				}
			}
		}
		else {
			telemetry.logEvent('hierarchy.copy-json', { source: 'commandPalette' });
			nodes = hierarchyService.getSelectedNodes();
			if (!nodes?.length) nodes = hierarchyService.roots;
		}

		try {
			logger.info(`Copying ${nodes.length} nodes to clipboard.`, nodes.map(n => n.name || n.id));
			const hierarchyObj = nodes.map(root => serializeNode(root));
			const hierarchyJSON = safeStringify(hierarchyObj);
			await clipboard.writeText(hierarchyJSON);
			if (showNotification) {
				notification.showNotification({
					type: 'info',
					message: `Copied ${nodes.length} node(s) to clipboard as JSON.`,
					position: `cursor`,
				});
			}
		}
		catch (err) {
			telemetry.logEvent('hierarchy.copy-json.failed', { reason: 'exception', message: (err instanceof Error ? err.message : String(err)).substring(0, 500) });
			logger.error('Failed to copy hierarchy to clipboard.', err);
			notification.showNotification({
				type: 'error',
				message: 'Failed to copy hierarchy to clipboard: ' + (err instanceof Error ? err.message : String(err)).substring(0, 200),
				position: "toast",
			});
		}
	}
}

CommandsRegistry.registerCommand(new CopyHierarchyCommand());

MenuRegistry.appendMenuItem(MenuId.ExplorerContextCopySubmenu, {
	command: {
		id: CopyHierarchyCommand.ID,
		title: 'Copy Hierarchy as JSON',
		icon: { id: 'copy' }
	},
	when: undefined,
	order: 10,
	group: '10_clipboard',
})

MenuRegistry.appendMenuItem(MenuId.HierarchyHeaderContext, {
	command: {
		id: CopyHierarchyCommand.ID,
		title: 'Copy Hierarchy as JSON',
		icon: { id: 'copy' }
	},
	when: undefined,
	order: 10,
	group: '10_clipboard',
})



// #region SearchHierarchyCommand

export interface SearchHierarchyCommandArgs {
	query: string;
	maxResults?: number;
	offset?: number;
	nodeType?: string;
	includeChildren?: boolean;
}

export class SearchHierarchyCommand extends Command<SearchHierarchyCommandArgs> {

	static readonly ID = 'hierarchy.search.nodes';

	constructor(
	) {
		super(SearchHierarchyCommand.ID, 'Search Hierarchy Nodes', {
			description: 'Search the hierarchy for nodes matching the query string (case-insensitive, partial match). The nodeType filter supports regex patterns for flexible type matching. Returns structured node information with pagination support.',
			args: {
				name: 'args',
				schema: {
					type: 'object',
					description: 'The arguments for searching the hierarchy',
					properties: {
						query: {
							type: 'string',
							description: 'The search query. Multiple terms can be separated by commas'
						},
						maxResults: {
							type: 'number',
							description: 'Maximum number of results to return. Default is 100.',
							default: 100
						},
						offset: {
							type: 'number',
							description: 'Number of matching nodes to skip before returning results (for pagination). Default is 0.',
							default: 0
						},
						nodeType: {
							type: 'string',
							description: 'Optional filter by node type. Supports regex patterns for flexible matching. Examples: "Mesh" (exact match), "Light.*" (matches Light, LightProbe, etc.), "Mesh|Camera" (matches either). Case-insensitive.'
						},
						includeChildren: {
							type: 'boolean',
							description: 'Whether to include information about child nodes. Default is false.',
							default: false
						}
					},
					required: ['query'],
				}
			},
		});
	}

	canExecute(args: unknown) {
		return { canExecute: true };
	}

	async execute(accessor: ServicesAccessor, args: SearchHierarchyCommandArgs): Promise<Array<IHierarchyNode>> {
		const hierarchyService = accessor.get(IHierarchyService);
		const logger = accessor.get(ILogService);

		if (!hierarchyService) {
			logger.error('Hierarchy service not available.');
			return [];
		}

		if(!args.query) {
			throw new Error('Search query is required.');
		}

		const query = args.query.split(',').map(q => q.trim()).filter(q => q.length > 0);
		const maxResults = args.maxResults || 100;
		const offset = args.offset || 0;
		const nodeTypeRegex = args.nodeType ? new RegExp(args.nodeType, "i") : null;

		// Search with a higher limit to account for filtering and offset
		const searchLimit = nodeTypeRegex ? maxResults * 3 : maxResults + offset;
		let allResults = await hierarchyService.search(query, searchLimit);

		// Apply type filter if specified
		if (nodeTypeRegex) {
			allResults = allResults.filter(n => n.type && nodeTypeRegex.test(n.type));
		}

		// Apply pagination
		const results = allResults.slice(offset, offset + maxResults);

		logger.info(`Search for "${args.query}" found ${allResults.length} result(s), returning ${results.length} after pagination.`);
		return results;
	}

	async executeForAI(accessor: ServicesAccessor, args: SearchHierarchyCommandArgs) {
		const hierarchyService = accessor.get(IHierarchyService);
		if (!hierarchyService) {
			return JSON.stringify({
				error: "Hierarchy service not available",
				results: []
			});
		}

		const query = args.query.split(',').map(q => q.trim()).filter(q => q.length > 0);
		const maxResults = args.maxResults || 100;
		const offset = args.offset || 0;
		const nodeTypeRegex = args.nodeType ? new RegExp(args.nodeType, "i") : null;
		const includeChildren = args.includeChildren ?? false;

		// Search with a higher limit to account for filtering and offset
		const searchLimit = nodeTypeRegex ? maxResults * 3 : maxResults + offset;
		let allResults = await hierarchyService.search(query, searchLimit);

		// Apply type filter if specified
		if (nodeTypeRegex) {
			allResults = allResults.filter(n => n.type && nodeTypeRegex.test(n.type));
		}

		const totalMatched = allResults.length;

		// Apply pagination
		const results = allResults.slice(offset, offset + maxResults);

		const formattedResults = results.map(n => {
			const nodeData: any = {
				id: n.id,
				name: n.name,
				type: n.type,
				isSelected: n.selected,
				isVisible: n.visible,
				isExpanded: n.expanded,
				parentId: n.parent?.id || null,
				childrenCount: n.children.length
			};

			if (includeChildren && n.children.length > 0) {
				nodeData.children = n.children.map(child => ({
					id: child.id,
					name: child.name,
					type: child.type
				}));
			}

			if (n.searchTags && n.searchTags.length > 0) {
				nodeData.tags = n.searchTags;
			}

			return nodeData;
		});

		return safeStringify({
			totalReturned: results.length,
			totalMatched: totalMatched,
			hasMore: totalMatched > (offset + results.length),
			pagination: {
				offset,
				limit: maxResults
			},
			filters: {
				query: args.query,
				nodeType: args.nodeType,
				includeChildren
			},
			results: formattedResults,
		});
	}
}

CommandsRegistry.registerCommand(new SearchHierarchyCommand());